'use client';

import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { Select } from '@/shared/ui/select';
import { FormFieldMessage } from './form-field-message';
import { FormFieldOptions, FormFieldProps } from './types';
import { getFormFieldState } from './utils/getFormFieldState';
import { mergeRefs } from './utils/mergeRefs';

type Props = FormFieldProps<typeof Select.Trigger> & {
    triggerContent: ReactNode;
    options: FormFieldOptions;
};

export const FormSelect = ({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    messageWrapperProps,
    isErrorVisible = true,
    isSuccessVisible,
    triggerContent,
    options,
    ref,
    ...props
}: Props) => (
    <Controller
        {...{ name, control, defaultValue, disabled, rules, shouldUnregister }}
        render={({
            field: { ref: fieldRef, onChange, value: selectedValue },
            fieldState: { error, isDirty, isTouched, invalid },
        }) => {
            const state = getFormFieldState({
                invalid,
                isDirty,
                isTouched,
                isErrorVisible,
                isSuccessVisible,
            });

            return (
                <>
                    <Select defaultValue={selectedValue} onValueChange={onChange}>
                        <Select.Trigger state={state} ref={mergeRefs([ref, fieldRef])} {...props}>
                            {triggerContent}
                        </Select.Trigger>
                        <Select.Content state={state}>
                            {typeof options === 'function'
                                ? options(onChange)
                                : options.map(({ label, value }) => (
                                      <Select.Item key={value} value={value}>
                                          {label}
                                      </Select.Item>
                                  ))}
                        </Select.Content>
                    </Select>
                    <FormFieldMessage state={state} error={error} messageWrapperProps={messageWrapperProps} />
                </>
            );
        }}
    />
);
